import { gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import moment from 'moment';
import { parse } from 'query-string';
import React from 'react';
import { Redirect, useLocation } from 'react-router';

import { ServiceRangeUnavailable } from 'src/app/graph/errors/serviceRangeUnavailable/ServiceRangeUnavailable';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useQueryParam } from 'src/hooks/useQueryParam';
import Config from 'src/lib/config';
import {
  TimeRangeOption,
  defaultTimeRange,
} from 'src/lib/config/timeRangeOptions';
import { date } from 'src/lib/date/date';
import * as ApolloTypes from 'src/lib/graphqlTypes/types';
import { mergeQueryParams } from 'src/lib/routing';

export const TIME_RANGE_ERRORS = {
  NOT_AVAILABLE: 'not-available',
  INVALID: 'invalid',
};

export interface TimeRangeInfo {
  accountCapabilities: {
    maxRangeInDays: number;
    ranges: ReadonlyArray<string>;
  };
  selectedRange: TimeRangeOption;
  timeFrom: Date | string;
  timeTo: Date | string;
  queryParams: { [queryParamName: string]: string };
}

type Props = {
  serviceId: string;
  children: (props: TimeRangeInfo) => React.ReactElement;
};

export const timeRangeQuery = gql`
  query UI__TimeRangeProviderQuery($serviceId: ID!) {
    service(id: $serviceId) {
      id
      account {
        id
      }
    }
  }
`;

export function getSelectedTimeRange({
  allowedRanges,
  defaultRange = defaultTimeRange,
  maxRangeInDays,
  queryString,
}: {
  allowedRanges: Array<TimeRangeOption>;
  defaultRange?: TimeRangeOption;
  maxRangeInDays: number;
  queryString: string;
}): { selectedRange: TimeRangeOption; to?: string; from?: string } {
  const { range, from, to } = parse(queryString);
  if (Array.isArray(to) || Array.isArray(from)) {
    throw new Error(TIME_RANGE_ERRORS.INVALID);
  }

  const selectedRange =
    (typeof range === 'string' && (range as TimeRangeOption)) ||
    (allowedRanges.includes(defaultRange) ? defaultRange : allowedRanges[0]);

  if (!selectedRange || !allowedRanges.includes(selectedRange)) {
    console.warn(`time range "${selectedRange}" unavailable`); // eslint-disable-line no-console
    throw new Error(TIME_RANGE_ERRORS.NOT_AVAILABLE);
  }
  if (selectedRange === 'custom') {
    try {
      // this will throw if validation fails
      date.validateDateSelection(
        moment(from ?? undefined).toDate(),
        moment(to ?? undefined).toDate(),
        moment.duration(maxRangeInDays, 'days'),
      );
    } catch (e) {
      const error = e as Error;
      console.warn(error.message); // eslint-disable-line no-console
      throw new Error(TIME_RANGE_ERRORS.INVALID);
    }
  }
  return { selectedRange, to: to ?? undefined, from: from ?? undefined };
}

export const TimeRangeProvider = ({ serviceId, children }: Props) => {
  const location = useLocation();
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const fastModeRange = useQueryParam('fastModeRange');

  return (
    <Query<
      ApolloTypes.UI__TimeRangeProviderQuery,
      ApolloTypes.UI__TimeRangeProviderQueryVariables
    >
      query={timeRangeQuery}
      variables={{
        serviceId,
      }}
    >
      {({ data, loading, error }) => {
        if (error || currentPlan.error)
          console.warn(error || currentPlan.error); // eslint-disable-line no-console
        if (loading || currentPlan.loading || (data && !data.service))
          return null;

        const capabilities = currentPlan.currentPlan || null;
        const maxRangeInDays =
          capabilities && capabilities.maxRangeInDays !== null
            ? capabilities.maxRangeInDays
            : Config.fallbackPlanOptions.maxRangeInDays;
        const ranges =
          (capabilities && capabilities.ranges) ||
          Config.fallbackPlanOptions.ranges;
        try {
          const { selectedRange, to, from } = getSelectedTimeRange({
            allowedRanges: ranges as TimeRangeOption[],
            maxRangeInDays,
            queryString: location.search,
          });

          const { timeFrom, timeTo } = date.getDateBounds(
            selectedRange,
            {
              from,
              to,
            },
            undefined,
            fastModeRange,
          );
          return children({
            selectedRange,
            accountCapabilities: {
              maxRangeInDays,
              ranges,
            },
            timeFrom,
            timeTo,
            queryParams: {
              [Config.queryParameters.SelectedTimeRange]: selectedRange,
              ...(selectedRange === 'custom' && from && to
                ? {
                    [Config.queryParameters.SelectedTimeRangeFrom]: from,
                    [Config.queryParameters.SelectedTimeRangeTo]: to,
                  }
                : null),
            },
          });
        } catch (e) {
          if ((e as Error).message === TIME_RANGE_ERRORS.INVALID) {
            return (
              <Redirect
                to={{
                  ...location,
                  search: mergeQueryParams(location.search, {
                    [Config.queryParameters.SelectedTimeRange]: undefined,
                    [Config.queryParameters.SelectedTimeRangeFrom]: undefined,
                    [Config.queryParameters.SelectedTimeRangeTo]: undefined,
                    [Config.queryParameters.FastModeRange]: undefined,
                  }),
                }}
              />
            );
          }

          return <ServiceRangeUnavailable />;
        }
      }}
    </Query>
  );
};
